// Page du jour d'un chantier (incrément 4, conception § 3.6) : qui était affecté, ce qui a été fait (par qui, avec qui, à quelle
// heure), les consignes de relève et le temps passé déclaré. En LECTURE : une erreur se corrige par une annulation motivée, tracée
// (la coche reste visible, barrée). Ce que chacun voit est décidé par le serveur (équipe et temps des autres : ceux qui encadrent,
// ou l'équipe du jour pour l'équipe). Aucun montant.
// Incrément 5 : réserves ouvertes ou levées ce jour-là, mesures, pièces posées, photos ; le jour même, « Réserve », « Photo »,
// « Mesure » et « Pièce posée » ouvrent leur volet (?volet=…), comme depuis Ma journée.
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Camera, ChevronLeft, ChevronRight, Clock, FlaskConical, Gauge, MessageSquareText, Package, Users } from 'lucide-react';
import { api, messageErreur } from '../../../lib/api.ts';
import { nombre, pluriel } from '../../../lib/format.ts';
import { Alerte, Badge, Bouton, Champ, ChampTexte, Chargement, Dialogue, EntetePage, EtatErreur, EtatVide, FilContexte, LienBouton, Panneau, useNotifier } from '../../../components/ui.tsx';
import { heures, quantite } from './commun.tsx';
import { CRENEAUX, jourLong } from './jours.ts';
import { heureMinute, jourHeure, nouvelEnvoi } from './Journal.tsx';
import { urlPhoto } from './PhotoChantier.tsx';
import { DialogueLever, estVolet, ListePiecesPosees, ListeReservesJour, VoletTerrain, type Volet } from './Terrain.tsx';
import type { CadreSaisie, PageJour as Page, Reserve } from './types.ts';
import { libelleChantier } from './saisie.ts';

/** Annulation motivée d'une coche ou retrait d'une consigne : le motif est obligatoire et conservé. */
function DialogueMotif({ titre, texte, action, url, fermer }: { titre: string; texte: string; action: string; url: string; fermer: () => void }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [op] = useState(nouvelEnvoi);
  const valider = async () => {
    setErreur(null); setEnvoi(true);
    try {
      await api.post(url, { motif, clientOpId: op });
      notifier('Enregistré : l’élément reste visible, barré, avec votre motif.');
      await Promise.all([qc.invalidateQueries({ queryKey: ['chantier-jour'] }), qc.invalidateQueries({ queryKey: ['chantier-avancement'] }),
        qc.invalidateQueries({ queryKey: ['ma-journee'] })]);
      fermer();
    } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre={titre} ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Retour</Bouton><Bouton variante="danger" chargement={envoi} disabled={motif.trim().length < 5} onClick={valider}>{action}</Bouton></>}>
      <div className="pile">
        <p className="petit" style={{ margin: 0 }}>{texte}</p>
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <ChampTexte libelle="Motif (obligatoire)" rows={2} value={motif} onChange={(e) => setMotif(e.target.value)} maxLength={1000} autoFocus />
      </div>
    </Dialogue>
  );
}

/** Déclarer ou corriger le temps d'une personne (responsable, référent, chargé d'affaires) : motif obligatoire pour une correction. */
function DialogueCorrigerTemps({ chantierId, jour, personne, posteId, actuel, fermer }:
  { chantierId: string; jour: string; personne: { id: string; nom: string }; posteId: string | null; actuel: string | null; fermer: () => void }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const [heures, setHeures] = useState(actuel ? String(Number(actuel)).replace('.', ',') : '');
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [op] = useState(nouvelEnvoi);
  const valider = async () => {
    setErreur(null); setEnvoi(true);
    try {
      await api.post(`/api/chantiers/${chantierId}/temps`, { jour, heures, personneId: personne.id, posteId, motif: motif || null, clientOpId: op });
      notifier(actuel ? 'Temps corrigé : la déclaration précédente reste dans l’historique.' : 'Temps déclaré.');
      await Promise.all([qc.invalidateQueries({ queryKey: ['chantier-jour'] }), qc.invalidateQueries({ queryKey: ['chantier-avancement'] })]);
      fermer();
    } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre={`${actuel ? 'Corriger' : 'Déclarer'} le temps de ${personne.nom}`} ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Retour</Bouton><Bouton variante="principal" chargement={envoi} disabled={!heures.trim() || (!!actuel && motif.trim().length < 5)} onClick={valider}>Valider</Bouton></>}>
      <div className="pile">
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <Champ libelle="Heures passées ce jour-là" inputMode="decimal" value={heures} onChange={(e) => setHeures(e.target.value)} autoFocus
          aide={actuel ? `Actuellement : ${String(Number(actuel)).replace('.', ',')} h. Saisir 0 si le temps a été déclaré par erreur.` : 'Exemple : 7 ou 6,5.'} />
        <ChampTexte libelle={actuel ? 'Motif (obligatoire)' : 'Motif (facultatif)'} rows={2} value={motif} onChange={(e) => setMotif(e.target.value)} maxLength={1000} />
      </div>
    </Dialogue>
  );
}

export function PageJour() {
  const { id, jour } = useParams();
  const q = useQuery({ queryKey: ['chantier-jour', id, jour], queryFn: () => api.get<Page>(`/api/chantiers/${id}/jours/${jour}`) });
  const [motif, setMotif] = useState<null | { titre: string; texte: string; action: string; url: string }>(null);
  const [temps, setTemps] = useState<null | { personne: { id: string; nom: string }; posteId: string | null; actuel: string | null }>(null);
  const [lever, setLever] = useState<Reserve | null>(null);
  // Volets de saisie du jour même (incrément 5) : ?volet=reserve|photo|mesure|piece.
  const [params, setParams] = useSearchParams();
  const brutVolet = params.get('volet');
  const ouvrirVolet = (v: Volet) => { const n = new URLSearchParams(params); n.set('volet', v); setParams(n); };
  const fermerVolet = () => { const n = new URLSearchParams(params); n.delete('volet'); setParams(n, { replace: true }); };
  if (q.isLoading) return <Chargement />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const p = q.data!;
  const c = p.chantier;
  const t = p.terrain;
  const cadre: CadreSaisie | null = p.droits.saisir && t.structure ? { chantierId: c.id, postes: t.structure, posteId: t.structure.length === 1 ? t.structure[0].id : null,
    libelle: libelleChantier(c) } : null;
  const reserves = [...t.reserves_ouvertes, ...t.reserves_levees];
  const valables = p.coches.filter((x) => !x.annulee);
  const base = `/interventions/chantiers/${c.id}`;
  // Corriger ou déclarer le temps d'autrui : chantier.corriger (responsable du chantier compris), jamais un jour à venir.
  // Réglages de la société (confidentialité des salariés) : temps et photos désactivables ; l'existant reste lisible de l'encadrement.
  const tempsActif = p.reglages?.temps !== false;
  const photosActives = p.reglages?.photos !== false;
  const ecrireTemps = tempsActif && p.droits.corriger && ['lance', 'suspendu'].includes(c.status) && p.jour <= p.aujourdhui;
  const sansTemps = [...new Map((p.affectes ?? []).filter((x) => !p.temps.some((t) => t.personne.id === x.id && !t.poste_id)).map((x) => [x.id, x])).values()];
  return (
    <div className="pile-l jr-page">
      <EntetePage
        fil={<FilContexte noeuds={[{ type: '', libelle: 'Chantiers', to: '/interventions/chantiers' }, { type: '', libelle: c.reference ?? c.title, to: `${base}?onglet=avancement` },
          { type: '', libelle: 'Page du jour' }]} />}
        titre={<span className="jr-titre">{jourLong(p.jour)}</span>}
        sousTitre={<span className="rangee" style={{ gap: 8 }}>{c.reference && <span className="mono">{c.reference}</span>}<span className="jr-chantier">{c.title}</span>
          {c.demo && <Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démo</Badge>}</span>} />

      {cadre && (
        <section className="mj-terrain jr-saisir" aria-label="Saisir sur le terrain aujourd’hui">
          <Bouton icone={<AlertTriangle aria-hidden />} onClick={() => ouvrirVolet('reserve')}>Réserve</Bouton>
          {photosActives && <Bouton icone={<Camera aria-hidden />} onClick={() => ouvrirVolet('photo')}>Photo</Bouton>}
          <Bouton icone={<Gauge aria-hidden />} onClick={() => ouvrirVolet('mesure')}>Mesure</Bouton>
          <Bouton icone={<Package aria-hidden />} onClick={() => ouvrirVolet('piece')}>Pièce posée</Bouton>
        </section>
      )}

      {!p.travaille && <EtatVide titre="Aucune activité ce jour-là" texte="Ni affectation, ni coche, ni consigne, ni temps déclaré, ni réserve, mesure, pièce posée ou photo." />}

      {p.affectes !== null && p.affectes.length > 0 && (
        <Panneau titre={<span className="jr-titre-panneau"><Users aria-hidden />Équipe du jour</span>}>
          <ul className="jr-liste">
            {p.affectes.map((x, i) => (
              <li key={`${x.id}-${i}`}><strong>{x.nom}</strong><span className="petit secondaire"> — {x.poste ?? 'tout le chantier'}{x.creneau !== 'journee' ? `, ${CRENEAUX[x.creneau].toLowerCase()}` : ''}
                {x.heures_prevues ? `, ${heures(x.heures_prevues)} prévues` : ''}</span></li>
            ))}
          </ul>
        </Panneau>
      )}

      <Panneau titre={`Fait ce jour-là — ${pluriel(valables.length, 'coche')}`}>
        {p.coches.length === 0 ? <p className="petit secondaire" style={{ margin: 0 }}>Aucune étape cochée ce jour-là.</p> : (
          <ol className="jr-coches">
            {p.coches.map((x) => (
              <li key={x.id} className={x.annulee ? 'jr-annulee' : undefined}>
                <div className="jr-coche-texte">
                  <span className="jr-coche-titre"><span className="chiffre jr-heure">{heureMinute(x.a)}</span>
                    <span className="jr-etape">{x.etape.label}</span></span>
                  <span className="petit secondaire">{x.etape.poste ? `${x.etape.poste} — ` : ''}{x.sorte === 'partiel' && x.quantite ? `${quantite(x.quantite, x.unite ?? '')} faits` : 'Faite'}
                    {' '}par {x.realisee_par.map((r) => r.nom).join(', ')}{x.realisee_par.length > 1 || x.realisee_par[0]?.id !== x.declaree_par.id ? ` (cochée par ${x.declaree_par.nom})` : ''}</span>
                  {x.aussi && <span className="petit secondaire">Aussi déclarée faite (deux coches conservées).</span>}
                  {x.note && <span className="petit">« {x.note} »</span>}
                  {x.sans_photo && <span className="petit secondaire">Photo non prise : {x.sans_photo}</span>}
                  <span className="rangee" style={{ gap: 6 }}>
                    {x.hors_poste && <Badge ton="ambre" icone={false}>Hors de son poste</Badge>}
                    {x.heure_incoherente && <Badge ton="ambre" icone={false}>Heure de l’appareil incohérente</Badge>}
                    {x.sans_photo && <Badge ton="ambre" icone={false} titre={x.sans_photo}>Sans la photo demandée</Badge>}
                    <span className="tres-petit discret expert-seulement">Reçue par le serveur {jourHeure(x.recue_a)}</span>
                  </span>
                  {x.annulee && <span className="petit jr-motif">Annulée par {x.annulee.par} le {jourHeure(x.annulee.le)} : {x.annulee.motif}</span>}
                </div>
                {x.annulable && (
                  <Bouton compact variante="fantome" onClick={() => setMotif({ titre: 'Annuler cette coche', action: 'Annuler la coche',
                    texte: `« ${x.etape.label} », ${heureMinute(x.a)}. La coche restera visible, barrée, avec votre motif.`, url: `/api/chantiers/${c.id}/coches/${x.id}/annuler` })}>Annuler</Bouton>
                )}
              </li>
            ))}
          </ol>
        )}
        {p.annulations_du_jour.length > 0 && (
          <div className="pile" style={{ gap: 'var(--e-1)', marginTop: 'var(--e-3)' }}>
            <strong className="petit">Annulations faites ce jour-là (coches d’autres jours)</strong>
            <ul className="jr-liste petit">
              {p.annulations_du_jour.map((a) => (
                <li key={a.id}>« {a.coche.etape} » cochée le <Link to={`${base}/jour/${a.coche.jour}`}>{a.coche.jour.split('-').reverse().slice(0, 2).join('/')}</Link> par {a.coche.par} — annulée par {a.par} : {a.motif}</li>
              ))}
            </ul>
          </div>
        )}
      </Panneau>

      <Panneau titre={<span className="jr-titre-panneau"><MessageSquareText aria-hidden />Consignes de relève</span>}>
        {p.consignes.length === 0 ? <p className="petit secondaire" style={{ margin: 0 }}>Aucune consigne laissée ce jour-là.</p> : (
          <ul className="jr-liste">
            {p.consignes.map((n) => (
              <li key={n.id} className={`jr-consigne ${n.retiree ? 'jr-annulee' : ''}`}>
                <div className="jr-coche-texte">
                  <span className="petit secondaire">{n.poste ?? 'Tout le chantier'} — {n.par.nom}, {heureMinute(n.le)}</span>
                  <span className="jr-texte">{n.texte}</span>
                  {n.retiree && <span className="petit jr-motif">Retirée par {n.retiree.par} le {jourHeure(n.retiree.le)} : {n.retiree.motif}</span>}
                </div>
                {n.retirable && (
                  <Bouton compact variante="fantome" onClick={() => setMotif({ titre: 'Retirer cette consigne', action: 'Retirer la consigne',
                    texte: 'La consigne restera visible, barrée, avec votre motif ; elle ne s’affichera plus en tête du poste.', url: `/api/chantiers/${c.id}/releves/${n.id}/retirer` })}>Retirer</Bouton>
                )}
              </li>
            ))}
          </ul>
        )}
      </Panneau>

      {reserves.length > 0 && (
        <Panneau titre={<span className="jr-titre-panneau"><AlertTriangle aria-hidden />Réserves et aléas — {pluriel(t.reserves_ouvertes.length, 'ouverte')}{t.reserves_levees.length ? `, ${pluriel(t.reserves_levees.length, 'levée')} (ouvertes un autre jour)` : ''}</span>}>
          <ListeReservesJour chantierId={c.id} reserves={reserves} lever={setLever} />
        </Panneau>
      )}

      {t.mesures.length > 0 && (
        <Panneau titre={<span className="jr-titre-panneau"><Gauge aria-hidden />Mesures — {t.mesures.filter((x) => !x.retiree).length}</span>}>
          <ul className="jr-liste">
            {t.mesures.map((x) => (
              <li key={x.id} className={`jr-consigne ${x.retiree ? 'jr-annulee' : ''}`}>
                <div className="jr-coche-texte">
                  <span className="jr-coche-titre"><span className="chiffre jr-heure">{heureMinute(x.le)}</span>
                    <span className="jr-etape">{x.point} : <span className="chiffre">{nombre(x.valeur, 4)} {x.unite}</span></span></span>
                  <span className="petit secondaire">{[x.poste, x.etape, x.equipement?.label].filter(Boolean).join(' — ') || 'Chantier'} — {x.moi ? 'vous' : x.par}</span>
                  {x.retiree && <span className="petit jr-motif">Retirée par {x.retiree.par} le {jourHeure(x.retiree.le)} : {x.retiree.motif}</span>}
                </div>
                {x.retirable && (
                  <Bouton compact variante="fantome" onClick={() => setMotif({ titre: 'Retirer cette mesure', action: 'Retirer la mesure',
                    texte: 'La mesure restera visible, barrée, avec votre motif.', url: `/api/chantiers/${c.id}/mesures/${x.id}/retirer` })}>Retirer</Bouton>
                )}
              </li>
            ))}
          </ul>
        </Panneau>
      )}

      {t.pieces.length > 0 && (
        <Panneau titre={<span className="jr-titre-panneau"><Package aria-hidden />Pièces posées — {t.pieces.length}</span>}>
          <ListePiecesPosees chantierId={c.id} pieces={t.pieces} />
        </Panneau>
      )}

      {t.photos.length > 0 && (
        <Panneau titre={<span className="jr-titre-panneau"><Camera aria-hidden />Photos — {t.photos.length}</span>}>
          <ul className="tr-photos-jour">
            {t.photos.map((x, i) => (
              <li key={x.id}>
                <a href={urlPhoto(c.id, x.id)} target="_blank" rel="noopener" aria-label={`Ouvrir la photo ${i + 1} en grand (${x.rattachement})`}>
                  <img src={urlPhoto(c.id, x.id)} alt={`Photo ${i + 1} — ${x.rattachement}`} loading="lazy" /></a>
                <span className="petit">{x.rattachement}</span>
                <span className="tres-petit discret">{x.moi ? 'Vous' : x.par}, {heureMinute(x.le)}</span>
                {x.retirable && (
                  <Bouton compact variante="fantome" onClick={() => setMotif({ titre: 'Retirer cette photo', action: 'Retirer la photo',
                    texte: 'La photo sera effacée (personne visible, erreur…) ; une trace du retrait et de votre motif reste dans le journal.', url: `/api/chantiers/${c.id}/photos/${x.id}/retirer` })}>Retirer</Bouton>
                )}
              </li>
            ))}
          </ul>
        </Panneau>
      )}

      {(tempsActif || p.temps.length > 0) && <Panneau titre={<span className="jr-titre-panneau"><Clock aria-hidden />Temps passé déclaré</span>}>
        {!tempsActif && <p className="petit secondaire" style={{ margin: '0 0 var(--e-2)' }}>La déclaration du temps passé est désactivée par votre entreprise :
          les temps déjà déclarés restent visibles de l’encadrement, rien ne se déclare plus.</p>}
        {p.temps.length === 0 ? <p className="petit secondaire" style={{ margin: 0 }}>{p.temps_partiel ? 'Vous n’avez pas déclaré de temps ce jour-là.' : 'Aucun temps déclaré ce jour-là.'}</p> : (
          <ul className="jr-liste">
            {p.temps.map((t) => (
              <li key={t.id} className="jr-temps">
                <span className="jr-temps-ligne"><span><strong>{t.personne.nom}</strong>{t.poste ? <span className="petit secondaire"> — {t.poste}</span> : null} : <strong className="chiffre">{heures(t.heures)}</strong></span>
                  {ecrireTemps && <Bouton compact variante="fantome" onClick={() => setTemps({ personne: t.personne, posteId: t.poste_id, actuel: t.heures })}>Corriger</Bouton>}</span>
                {(t.declare_par !== t.personne.nom || t.motif) && <span className="petit secondaire">Déclaré par {t.declare_par}{t.motif ? ` : ${t.motif}` : ''}</span>}
                {t.historique.length > 0 && (
                  <details className="petit"><summary>Historique ({pluriel(t.historique.length, 'déclaration précédente', 'déclarations précédentes')})</summary>
                    <ul>{t.historique.map((h, i) => <li key={i}>{heures(h.heures)} — {h.par}, {jourHeure(h.le)}{h.motif ? ` : ${h.motif}` : ''}</li>)}</ul>
                  </details>
                )}
              </li>
            ))}
          </ul>
        )}
        {ecrireTemps && sansTemps.length > 0 && (
          <div className="rangee jr-declarer">
            {sansTemps.map((x) => <Bouton key={x.id} compact onClick={() => setTemps({ personne: x, posteId: null, actuel: null })}>Déclarer pour {x.nom}</Bouton>)}
          </div>
        )}
        <p className="tres-petit discret" style={{ margin: 'var(--e-2) 0 0' }}>{p.temps_partiel ? 'Vous voyez votre temps. ' : `Total déclaré : ${heures(p.temps_total)}. `}
          Suivi du chantier seulement : ce n’est pas le décompte légal du temps de travail.</p>
      </Panneau>}

      <div className="barre-actions barre-collante jr-barre">
        {p.precedent ? <LienBouton icone={<ChevronLeft aria-hidden />} to={`${base}/jour/${p.precedent}`}><span className="masque-etroit">Jour </span>précédent</LienBouton> : <span />}
        <LienBouton variante="fantome" to={`${base}?onglet=avancement`}>Avancement</LienBouton>
        {p.suivant ? <LienBouton to={`${base}/jour/${p.suivant}`}><span className="masque-etroit">Jour </span>suivant<ChevronRight aria-hidden /></LienBouton> : <span />}
      </div>
      {motif && <DialogueMotif {...motif} fermer={() => setMotif(null)} />}
      {temps && <DialogueCorrigerTemps chantierId={c.id} jour={p.jour} {...temps} fermer={() => setTemps(null)} />}
      {lever && <DialogueLever chantierId={c.id} chantierLibelle={libelleChantier(c)} reserve={lever} fermer={() => setLever(null)} />}
      {cadre && estVolet(brutVolet) && (brutVolet !== 'photo' || photosActives) && <VoletTerrain key={brutVolet} volet={brutVolet} cadre={cadre} fermer={fermerVolet} />}
    </div>
  );
}
